import {
    Breadcrumbs,
    Button,
    Grid,
    IconButton, Paper, Skeleton,
    Stack,
    Switch, Table, TableBody,
    TableCell, TableContainer, TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import {EquipmentConnectionStatus} from "./components/EquipmentConnectionStatus";
import {checkPermissionsAndRedirect, hasPermission} from "../../../../configs/functions";
import {EditIcon, LockerIcon, PasswordResetIcon, StockIcon, TrashIcon, VendingMachineIcon} from "../../../common/Icons";
import {Add, History, Home, PictureAsPdf, RemoveRedEye} from "@mui/icons-material";
import {useAppContext} from "../../../../contexts/AppContext";
import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import {
    atualizaEquipamento,
    atualizaLocker, carregaConfigsEquipamento,
    carregaEquipamentos, carregaLockers,
    carregaPontosEntrega, deletaEquipamento,
    generateUpdateTokenEquipamento
} from "./configs/functions";
import {listaEmpresas} from "../../management/companies/configs/functions";
import {Helmet} from "react-helmet-async";
import LinkNavegacao from "../../../common/Link";
import PageTitle from "../../../common/PageTitle";
import {FiltersButton} from "../../../common/FiltersButton";
import {EquipmentsTableFilter} from "./components/EquipmentsTableFilter";
import {CustomToolbarExportTable} from "../../../common/CustomToolbarExportTable";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";
import {EmptyTableRow} from "../../../common/EmptyTableRow";
import {Pagination} from "../../../common/Pagination";
import {MachineForm} from "./components/MachineForm";
import ConfirmDialog from "../../../common/ConfirmDialog";
import {ConfirmGenerateTokenDialog} from "./components/ConfirmGenerateTokenDialog";
import {GenerateTokenDialog} from "./components/GenerateTokenDialog";
import {LastSyncCell} from "./components/LastSyncCell";
import {EquipmentHistoryDialog} from "./components/EquipmentHistoryDialog";
import {RestockingMachineDialog} from "./components/MachineForm/components/RestockingMachineDialog";
import {listaProdutos} from "../products/configs/functions";
import {motoresToReceiptRows} from "./configs/layoutAdapters";
import {exportConfigToPDF} from "./configs/utils";
import {extractErrorMessage} from "../../../../utils/extract-error-message";

const MachineRow = ({
                        data,
                        isSempher,
                        handleOpenEditForm,
                        handleOpenHistory,
                        handleUpdateStatus,
                        handleOpenDeleteDialog,
                        handleOpenConfirmGenerateTokenDialog,
                        handleOpenRestockingDialog,
                        handlePrintConfig,
                    }) => {
    const isInactive = !data.status;

    const row = (
        <TableRow>
            <TableCell>{data.nome}</TableCell>
            <TableCell>{data.tag_identificacao}</TableCell>
            <TableCell>{data.empresa.nome}</TableCell>
            <TableCell>{data.descricao}</TableCell>
            <TableCell>
                <EquipmentConnectionStatus
                    mode={data.mode}
                    status={data.status}
                />
            </TableCell>
            <TableCell>
                <LastSyncCell lastSyncAt={data.lastSyncAt} />
            </TableCell>
            <TableCell>
                <Switch
                    checked={!!data.status}
                    onChange={() => handleUpdateStatus('status', isInactive)}
                    disabled={
                        !isSempher ||
                        !hasPermission(['admin', 'admin_equipamentos', 'update_equipamentos'])
                    }
                />
            </TableCell>
            <TableCell>
                <Switch disable checked={!!data.acesso_facial} onChange={() => handleUpdateStatus('acesso_facial', !data.acesso_facial)} disabled={isInactive || !isSempher} />
            </TableCell>
            <TableCell>
                <Switch checked={!!data.acesso_retiradas} onChange={() => handleUpdateStatus('acesso_retiradas', !data.acesso_retiradas)} disabled={isInactive || !isSempher} />
            </TableCell>
            <TableCell align='right' style={{ minWidth: 200 }}>
                <IconButton
                    aria-label="Organizar Equipamento"
                    onClick={() => handleOpenRestockingDialog(data)}
                    disabled={isInactive}
                    sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}
                >
                    <StockIcon color="primary" />
                </IconButton>

                <IconButton
                    aria-label="Imprimir configurações"
                    onClick={() => handlePrintConfig(data)}
                    disabled={isInactive}
                    sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}
                >
                    <PictureAsPdf color="primary" />
                </IconButton>

                {isSempher && (
                    <>
                        <IconButton
                            aria-label="Visualizar histórico"
                            onClick={() => handleOpenHistory(data)}
                        >
                            <History color='primary' />
                        </IconButton>

                        <IconButton
                            aria-label="Gerar Token para Máquina"
                            onClick={() => handleOpenConfirmGenerateTokenDialog(data)}
                            disabled={isInactive}
                            sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}
                        >
                            <PasswordResetIcon />
                        </IconButton>
                    </>
                )}

                {isSempher ? (
                    <>
                        {hasPermission(["admin", "admin_equipamentos", "update_equipamentos"]) && (
                            <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)} disabled={isInactive} sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}>
                                <EditIcon />
                            </IconButton>
                        )}

                        {hasPermission(["admin", "admin_equipamentos", "delete_equipamentos"]) && (
                            <IconButton aria-label="Excluir" onClick={() => handleOpenDeleteDialog(data)} disabled={isInactive} sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}>
                                <TrashIcon />
                            </IconButton>
                        )}
                    </>
                ) : (
                    <Button onClick={() => handleOpenEditForm(data)} size='small' color="primary" variant='contained' sx={{minWidth: 30}}>
                        <RemoveRedEye fontSize='small'/>
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );

    if (isInactive) {
        return (
            <Tooltip
                title="Máquina inativa — ative-a para liberar as ações"
                placement="top"
                arrow
            >
                {row}
            </Tooltip>
        );
    }

    return row;
}

export function ListOfMachines() {
    const { dadosUsuario } = useAppContext();
    const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();

    const queryClient = useQueryClient();

    const [selectedMachine, setSelectedMachine] = useState(null);

    const [lastUpdated, setLastUpdated] = useState(false);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openGenerateTokenDialog, setOpenGenerateTokenDialog] = useState(false);
    const [openConfirmGenerateTokenDialog, setOpenConfirmGenerateTokenDialog] = useState(false);
    const [openMachineForm, setOpenMachineForm] = useState(false);
    const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
    const [openRestockingDialog, setOpenRestockingDialog] = useState(false);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();

    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const page = searchParamPage ? (parseInt(searchParamPage) - 1) : 0;
    const rowsPerPage = searchParamPerPage ? parseInt(searchParamPerPage) : 10;

    const columns = useMemo(() => [
        { field: 'nome', headerName: 'Nome' },
        { field: 'tag_ientificaco', headerName: 'Tag Identifcação' },
        { field: 'empresa.fantasyName', headerName: 'Empresa', valueGetter: (params) => params.row.empresa?.nome },
        { field: 'descricao', headerName: 'Descrição' },
        { field: 'mode', headerName: 'Conexão', valueGetter: (params) => params.row.mode === 'unknown' ? 'desconhecida' : params.row.mode },
        { field: 'lastSyncAt', headerName: 'Última sincronização', valueGetter: (params) => params.row.lastSyncAt },
        { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status ? 'Ativado' : 'Inativado' },
        { field: 'acesso_facial', headerName: 'Habilitar facial', valueGetter: (params) => params.row.acesso_facial },
        { field: 'acesso_retiradas', headerName: 'Habilitar retirada', valueGetter: (params) => params.row.acesso_retiradas },
    ], []);

    const queryFilters = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        nome: searchParams.get('nome'),
        id_empresas: [searchParams.get('id_empresas')],
        id_ponto_entrega: searchParams.get('id_ponto_entrega'),
        tipo: 'maquina',
        status: searchParams.get('status'),
    };

    const queryKey = ['listaMaquinas', queryFilters];
    const { data: machinesData, isLoading, isPending, isSuccess, isRefetching } = useQuery({
        queryKey,
        queryFn: () => carregaEquipamentos(queryFilters),
        refetchInterval: 1000 * 60 * 3,
        refetchIntervalInBackground: true,
    });

    const machines = machinesData?.data?.data ?? [];
    const numTotalItems = machinesData?.data?.numero_total ?? 0;
    const isLoadingList = isLoading || isPending;

    const { data: empresasData } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: ({ queryKey }) => {
            const [_key, parametro] = queryKey;
            return listaEmpresas(null, null, null, true);
        },
        staleTime: Infinity,
    });

    const { data: pontosDeEntregaData } = useQuery({
        queryKey: ['listPontosDeEntrega'],
        queryFn: ({ queryKey }) => {
            const [_key, parametro] = queryKey;
            return carregaPontosEntrega({ isFilter: true });
        },
        staleTime: Infinity,
    });

    const handleUpdateStatus = useMutation({
        mutationFn: (data) => atualizaEquipamento(data.id, data.dataToUpdate),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKey });
            // get from cached data, descobrir forma de encontrar qual item foi atualizado.

            if (typeof variables?.dataToUpdate.status === 'boolean') {
                const status = variables?.dataToUpdate.status ? 'ativada' : 'inativada';
                exibirAlerta('Sucesso', `Máquina ${status} com sucesso`, 'success');
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const { mutateAsync: handleGenerateUpdateToken, isPending: isLoadingUpdateToken } = useMutation({
        mutationFn: async (data) => await generateUpdateTokenEquipamento(data.id),
        onSuccess: (response) => {
            const token = response?.data?.token;

            if (token) {
                setSelectedMachine((prevState) => ({ ...prevState, token }));
                setOpenGenerateTokenDialog(true);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao gerar token';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const handleEdit = (machine) => {
        setSelectedMachine(machine);
        setOpenMachineForm(true);
    }

    const handleOpenDeleteDialog = (item) => {
        setSelectedMachine(item);
        setOpenDeleteDialog(true);
    }

    const handleOpenHistory = (machine) => {
        setSelectedMachine(machine);
        setOpenHistoryDialog(true);
    };

    const handleRestocking = (machine) => {
        setSelectedMachine(machine);
        setOpenRestockingDialog(true);
    }

    const handleOpenGenerateTokenDialog = async (item) => {
        try {
            exibirDialog('Gerando token. Aguarde...');
            setSelectedMachine(item);
            setOpenConfirmGenerateTokenDialog(false);
            // endpoint generate token
            await handleGenerateUpdateToken({ id: item.id });
        } catch (error) {
            console.error('ListOfMachine ~ error:', error);
        } finally {
            handleCloseDialog();
        }
    };

    const handlePrintConfig = async (machine) => {
        exibirDialog('Gerando PDF das configurações...');

        try {
            const [configRes, produtosRes] = await Promise.all([
                carregaConfigsEquipamento({ id_equipamento: machine.id }),
                listaProdutos({ id_empresa: machine.empresa?.id, for_machine: 1 }),
            ]);

            // pega a empresa COMPLETA (mesmo objeto usado nos outros relatórios)
            const empresaCompleta = empresasData?.data?.data?.find(
                (e) => e.id === machine.empresa?.id
            ) ?? null;

            exportConfigToPDF({
                linhas: motoresToReceiptRows(configRes?.data?.data ?? []),
                produtos: produtosRes?.data?.data ?? [],
                selectedEmpresa: empresaCompleta,
                dadosUsuario,
                titulo: machine.nome,
                unidadeLabel: 'Motores',
                reportName: 'configuracoes_maquina',
                reportTitle: 'CONFIGURAÇÕES DA MÁQUINA',
            });
        } catch (error) {
            exibirAlerta('Erro', 'Não foi possível gerar o PDF das configurações', 'error');
        } finally {
            handleCloseDialog();
        }
    };

    const { mutateAsync: handleDelete, isPending: isLoadingDelete } = useMutation({
        mutationFn: deletaEquipamento,
        onSuccess: (response, id) => {
            const message = response.data.message ?? 'Máquina excluída com sucesso';
            exibirAlerta('Sucesso', message, 'success');

            if (message === 'A máquina indicada possui relação com outros itens. Por isso foi inativada') {
                // como o equipamento não é excluído dependendo dos seus relacionamentos,
                // nao é possível saber se excluiu ou se atualizou o status.
                queryClient.invalidateQueries({ queryKey: queryKey });
            } else {
                const cached = queryClient.getQueryData(queryKey);
                const updatedData = {
                    ...cached,
                    data: {
                        ...cached.data,
                        data: cached.data.data.filter((item) => item.id !== id),
                    },
                };
                queryClient.setQueryData(queryKey, updatedData);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
        onSettled: () => {
            setOpenDeleteDialog(true);
            setSelectedMachine(null);
        },
    });

    useEffect(() => {
        checkPermissionsAndRedirect(['admin', 'admin_equipamentos', 'list_equipamentos']);

        setSearchParams((state) => {
            state.set('page', String(page + 1));
            state.set('perPage', String(rowsPerPage));
            return state;
        });
    }, [page, rowsPerPage, setSearchParams]);

    useEffect(() => {
        if (openMachineForm) return;
        setSelectedMachine(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);
    }, [openMachineForm, searchParams]);

    useEffect(() => {
        if (isSuccess && !isRefetching) {
            const date = new Date();
            const formattedDate = date.toLocaleString('pt-BR');
            setLastUpdated(formattedDate);
        }
    }, [isSuccess, isRefetching]);

    return (
        <>
            <Helmet title='Máquinas' defer={false} />

            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="end"
                spacing={3}
            >
                <Grid item xs={12} md={9}>
                    <Breadcrumbs>
                        <LinkNavegacao to="/"><Home fontSize="small" /></LinkNavegacao>
                        <LinkNavegacao to="/epis">Gestão de EPI's</LinkNavegacao>
                        <LinkNavegacao to="/epis/equipamentos">Equipamentos</LinkNavegacao>
                        <Typography variant='span'>Máquinas</Typography>
                    </Breadcrumbs>

                    <PageTitle
                        icon={<VendingMachineIcon fontSize='large' />}
                        title="Máquinas"
                        description={(dadosUsuario.isSempher ? "Gerencie" : "Visualize") + " as máquinas das empresas."}
                    />

                    <Grid item xs={12}>
                        <Stack direction='row' spacing={0.5} alignItems='center' p sx={{ pt: 0 }}>
                            <Typography variant='caption' color='textSecondary'>Última atualização: </Typography>
                            {isLoadingList || lastUpdated === null ? (
                                <Skeleton width={110} variant='text' />
                            ) : (
                                <Typography variant='caption' color='textSecondary'>
                                    {lastUpdated}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Stack direction={{ xs: 'column-reverse', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                        <FiltersButton
                            isOpenFilters={isOpenFilters}
                            setIsOpenFilters={setIsOpenFilters}
                            activeFilters={activeFilters}
                        />

                        {hasPermission(["admin", "admin_equipamentos", "create_equipamentos"]) && dadosUsuario.isSempher &&
                            <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={() => setOpenMachineForm(true)}
                                    disabled={!hasPermission(["admin", "admin_equipamentos", "create_equipamentos"])}
                                >
                                    Cadastrar Máquina
                                </Button>
                            </Typography>
                        }
                    </Stack>
                </Grid>

                <EquipmentsTableFilter
                    isOpenFilters={isOpenFilters}
                    isLoadingList={isLoadingList}
                    empresas={empresasData?.data?.data || []}
                    pontosDeEntrega={pontosDeEntregaData?.data?.data || []}
                />

                <Grid item xs={12}>
                    <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
                        <CustomToolbarExportTable
                            rows={machines}
                            columns={columns}
                            reportName="relatorio_maquinas"
                            reportPdfTitle="RELATÓRIO DE MÁQUINAS"
                            getDataWithoutPaginate={() => {
                                const { limit, offset, ...data } = queryFilters;
                                return carregaEquipamentos(data);
                            }}
                            getSelectedEmpresa={() => empresasData.data.data.find(empresa => empresa.id === Number(searchParams.get('id_empresas')))}
                        />

                        <Table aria-label="Máquinas">
                            <TableHead>
                                <TableRow>
                                    {/*<TableCell align="center">Tipo</TableCell>*/}
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Tag Identificação</TableCell>
                                    <TableCell>Empresa</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Conexão</TableCell>
                                    <TableCell>Última Sincronização</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Habilita facial</TableCell>
                                    <TableCell align="center">Habilita retirada</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingList ? (
                                    <SkeletonTableRow
                                        numCells={10}
                                        numRows={rowsPerPage < 20 ? rowsPerPage : 5}
                                        firstCellIsIcon
                                    />
                                ) : machines.length > 0 ? (
                                    machines.map((machine) => (
                                        <MachineRow
                                            key={machine.id}
                                            data={machine}
                                            isSempher={dadosUsuario?.isSempher ?? false}
                                            handleOpenEditForm={() => handleEdit(machine)}
                                            handleOpenHistory={() => handleOpenHistory(machine)}
                                            handleUpdateStatus={(field, value) => {
                                                const dataSendUpdate = {
                                                    id: machine.id,
                                                    dataToUpdate: {
                                                        [field]: value
                                                    }
                                                }
                                                handleUpdateStatus.mutateAsync(dataSendUpdate)
                                            }}
                                            handleOpenDeleteDialog={() => handleOpenDeleteDialog(machine)}
                                            handleOpenConfirmGenerateTokenDialog={() => handleOpenGenerateTokenDialog(machine)}
                                            handleOpenRestockingDialog={() => handleRestocking(machine)}
                                            handlePrintConfig={() => handlePrintConfig(machine)}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={10} />
                                )}
                            </TableBody>
                        </Table>

                        <Pagination
                            page={page}
                            rowsPerPage={rowsPerPage}
                            numTotalItems={numTotalItems}
                        />
                    </TableContainer>
                </Grid>
            </Grid>

            <MachineForm
                open={openMachineForm}
                setOpen={setOpenMachineForm}
                maquina={selectedMachine}
                empresas={empresasData?.data?.data ?? []}
                queryKey={queryKey}
                readOnly={!dadosUsuario.isSempher}
            />

            <ConfirmDialog
                title="Excluir Equipamento"
                description="Tem certeza que deseja excluir esta máquina?"
                goAction={() => handleDelete(selectedMachine.id)}
                handleClose={() => {
                    setOpenDeleteDialog(false);
                    setSelectedMachine(null);
                }}
                state={openDeleteDialog}
            />

            <ConfirmGenerateTokenDialog
                open={openConfirmGenerateTokenDialog}
                selectedEquipment={selectedMachine}
                goAction={() => handleOpenGenerateTokenDialog(selectedMachine)}
                handleClose={() => {
                    setOpenConfirmGenerateTokenDialog(false);
                    setSelectedMachine(null);
                }}
                isLoading={isLoadingUpdateToken}
            />

            <GenerateTokenDialog
                open={openGenerateTokenDialog}
                selectedEquipment={selectedMachine}
                handleClose={() => {
                    setOpenGenerateTokenDialog(false);
                    setSelectedMachine(null);
                }}
            />

            <EquipmentHistoryDialog
                open={openHistoryDialog}
                onClose={() => {
                    setOpenHistoryDialog(false);
                    setSelectedMachine(null);
                }}
                equipamento={selectedMachine}
                tipo="maquina"
            />

            <RestockingMachineDialog
                open={openRestockingDialog}
                onClose={() => {
                    setOpenRestockingDialog(false);
                    setSelectedMachine(null);
                }}
                maquina={selectedMachine}
            />
        </>
    );
}